import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useState } from "react";
import {
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { AppDispatch, RootState } from "../../../state/configureStore";
import { AddModal } from "../../../components/molecules/AddModal";
import { addAlert, AlertData } from "../../../state/thunks/machineAlertsThunks";
import { resetSavingStatus } from "../../../state/reducers/slices/machineAlertSlice";

interface AddAlertModalProps {
  onClose: () => void;
  open: boolean;
  serialId: string;
}

export const AddAlertModal = ({
  onClose,
  open,
  serialId,
}: AddAlertModalProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const { saving, alertTypes, lastError } = useSelector(
    (state: RootState) => state.machineAlert,
  );

  const [eventType, setEventType] = useState("");
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState({ email: false, eventType: false });

  useEffect(() => {
    if (saving === "saved" && open) {
      handleClose();
    }
  }, [saving, open]);

  const validateEmail = (email: string) => /\S+@\S+\.\S+/.test(email);

  const handleSave = async () => {
    const isEmailValid = validateEmail(email);
    const isTypeValid = eventType !== "";

    setErrors({ email: !isEmailValid, eventType: !isTypeValid });

    if (isEmailValid && isTypeValid) {
      const payload: Omit<AlertData, "alertId"> = {
        serialId: serialId,
        eventType: eventType,
        emailRecipient: email,
      };
      await dispatch(addAlert(payload));
    }
  };

  const handleClose = () => {
    dispatch(resetSavingStatus()); // <--- cleares "failed" in Redux (machineAlertSlice)
    setEventType("");
    setEmail("");
    setErrors({ email: false, eventType: false });
    onClose();
  };

  return (
    <AddModal
      title={"Add New Alert"}
      saving={saving}
      open={open}
      maxWidth="sm"
      savingFailedMessage={
        lastError ||
        "Saving failed. Please check your inputs below and your internet connection and try again."
      }
      closeModal={handleClose}
      onSave={handleSave}
    >
      <Box sx={{ minWidth: { xs: "100%", sm: "500px" }, py: 1 }}>
        {/* 1. Dropdown för Incident Type */}
        <FormControl fullWidth margin="normal" error={errors.eventType}>
          <InputLabel id="incident-type-label">Incident Type</InputLabel>
          <Select
            labelId="incident-type-label"
            value={eventType}
            label="Incident Type"
            onChange={(e) => {
              setEventType(e.target.value);
              setErrors((prev) => ({ ...prev, eventType: false }));
            }}
          >
            {alertTypes.map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </Select>
          {errors.eventType && (
            <FormHelperText>Please select an incident type.</FormHelperText>
          )}
        </FormControl>

        <TextField
          autoFocus
          margin="normal"
          id="email_input_field"
          label="Email Address"
          value={email}
          type="email"
          fullWidth
          error={errors.email}
          helperText={errors.email ? "Please enter a valid email." : null}
          onChange={(event) => {
            setEmail(event.target.value);
            if (errors.email) {
              setErrors((prev) => ({ ...prev, email: false }));
            }
          }}
        />
      </Box>
    </AddModal>
  );
};
